import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { type ErrorResponse } from '../codegen/ErrorResponse';
import { type RedirectResponse } from '../codegen/RedirectResponse';
import { type NavigateResponse } from '../codegen/NavigateResponse';

import { useReCaptchaContext } from './ReCaptchaContext';

/**
 * Parameters for @see useSubmit
 */
interface UseSubmitOptions<TSubmit> {
    /**
     * should be set to reCAPTCHA site key if we should be using captcha for this form.
     * if set, the page will have captcha enabled and reCAPTCHA token will be submitted
     * as captchaToken parameter
     */
    captchaKey?: string;

    /**
     * Antiforgery token to be submitted to the server with POST
     */
    antiforgeryToken?: string;

    /**
     * Optional callback that allows to massage data before it is submitted to the server.
     */
    preSubmit?: (data: TSubmit) => void;
}

/**
 * What is returned by @see useSubmit
 */
interface UseSubmitResult<TSubmit> {
    /**
     * Handler to be passed to form's handleSubmit
     */
    onSubmit: (data: TSubmit) => void | Promise<void>;

    /**
     * Form-level error message, if submission failed.
     */
    submitError?: string;

    /**
     * Allows to reset submit error.
     */
    resetSubmitError: () => void;

    /**
     * If set to true, we initiated a redirect. Appropriate UI elements can
     * be disabled/hidden to avoid interruption.
     */
    isRedirecting: boolean;
}

/**
 * Handler for SPA forms - sends JSON POST and handles JSON responses,
 * with optional but likely SPA React Router navigation, with additional passing
 * of acquired state.
 * @param {string} url request url
 * @param {UseSubmitOptions} options submit options
 * @returns {UseSubmitResult} onSubmit - to be passed to the form submit handler
 */
export function useSubmit<TSubmit>(
    url: string,
    options?: UseSubmitOptions<TSubmit>,
): UseSubmitResult<TSubmit> {
    const navigate = useNavigate();
    const { getCaptchaToken } = useReCaptchaContext(options?.captchaKey);
    const [submitError, setSubmitError] = useState<string>();
    const [isRedirecting, setIsRedirecting] = useState<boolean>(false);

    const preSubmit = options?.preSubmit;
    const antiforgeryToken = options?.antiforgeryToken;

    const onSubmit = useCallback(
        async (data: TSubmit) => {
            // if submitted data needs to be massaged
            preSubmit && preSubmit(data);

            // if captcha is not required, this will return nothing
            const captchaToken = await getCaptchaToken();
            const payload = !captchaToken
                ? data
                : { ...data, ...{ captchaToken } };

            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
            };
            if (antiforgeryToken)
                headers['RequestVerificationToken'] = antiforgeryToken;

            const response = await fetch(url, {
                method: 'POST',
                body: JSON.stringify(payload),
                headers,
                mode: 'same-origin',
                // ^ this likely makes all antiforging efforts unnecessary (in conjunction with all
                // our client scripts being corsed) but better be very safe than safe
                // https://github.com/dotnet/AspNetCore.Docs/issues/6831
                redirect: 'error',
                // ^ surprisingly, there is no way to resolve redirects manually (and 'manual' is not
                // what we think it is), so we fail here to fail early
                // https://stackoverflow.com/a/39739894
                cache: 'no-cache',
            });

            // ONEDAY: postSubmit?
            // ONEDAY: handle forbidden?

            // 429 Too Many Requests from the page-level throttler or WAF
            if (response.status == 429) {
                setSubmitError('Too many attempts, please try again later.');
                return;
            }

            // handle known application protocol responses
            if (response.status == 200) {
                // expected response is one of the following types:
                type SubmitResponse = ErrorResponse &
                    RedirectResponse &
                    NavigateResponse<unknown>;

                const json: SubmitResponse =
                    (await response.json()) as SubmitResponse;

                // application error
                if (json.error) {
                    const errorResponse = json as ErrorResponse;
                    setSubmitError(errorResponse.error);
                    // ONEDAY: per-field errors
                    return;
                }
                // 301/302 emulation to work around "fetch" limitations
                if (json.redirect) {
                    const redirectResponse = json as RedirectResponse;
                    setIsRedirecting(true);
                    window.location.href = redirectResponse.redirect;
                    return;
                }
                // application navigation via react router, with optional state
                if (json.location) {
                    const navigateResponse = json as NavigateResponse<unknown>;
                    navigate(navigateResponse.location, {
                        state: navigateResponse.state,
                    });
                    return;
                }
            }

            // anything else is unexpected
            setSubmitError('Unexpected error. Please try again later.');
            console.error(
                `Server returned ${response.status} ${response.statusText}`,
            );
            console.error(await response.text());
        },
        [getCaptchaToken, navigate, url, preSubmit, antiforgeryToken],
    );

    const resetSubmitError = useCallback(() => setSubmitError(undefined), []);

    return {
        onSubmit,
        submitError,
        isRedirecting,
        resetSubmitError,
    };
}
